import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { Car, Users, GraduationCap, Plane, Calendar, MapPin, Phone, CheckCircle2, ArrowRight, Clock, ShieldCheck, Heart } from 'lucide-react';
export function NonMedicalTransportation() {
  const services = [{
    title: 'Daily Errands & Appointments',
    icon: <Car className="w-8 h-8 text-primary-600" data-id="element-794" />,
    description: 'Grocery shopping, bank visits, and personal appointments.',
    features: ['Door-to-door service', 'Wait-and-return options', 'Help with bags/groceries']
  }, {
    title: 'Senior Transportation',
    icon: <Heart className="w-8 h-8 text-primary-600" data-id="element-795" />,
    description: 'Reliable rides for seniors to social activities, shopping, and community events.',
    features: ['Compassionate drivers', 'Wheelchair accessible', 'Extra boarding time']
  }, {
    title: 'School Runs for Kids',
    icon: <GraduationCap className="w-8 h-8 text-primary-600" data-id="element-796" />,
    description: 'Safe daily pick-up and drop-off for children, punctual and supervised.',
    features: ['Vetted, safe drivers', 'Consistent scheduling', 'Direct communication']
  }, {
    title: 'Airport Transportation',
    icon: <Plane className="w-8 h-8 text-primary-600" data-id="element-797" />,
    description: 'Comfortable rides to and from the airport, on-time guaranteed.',
    features: ['Luggage assistance', 'Flight tracking', 'Curbside drop-off']
  }, {
    title: 'Event Transportation',
    icon: <Calendar className="w-8 h-8 text-primary-600" data-id="element-798" />,
    description: 'Weddings, church, family gatherings, and special occasions.',
    features: ['Group transport options', 'Flexible return times', 'Clean, comfortable vehicles']
  }, {
    title: 'Long-Distance Trips',
    icon: <MapPin className="w-8 h-8 text-primary-600" data-id="element-799" />,
    description: 'Interstate and long-distance transportation for personal travel.',
    features: ['Comfortable seating', 'Rest stops as needed', 'Direct routes']
  }];
  const benefits = [{
    title: 'Professional Drivers',
    icon: <ShieldCheck className="w-6 h-6 text-primary-600" data-id="element-800" />,
    description: 'Fully vetted, background-checked, and trained for safety and courtesy.'
  }, {
    title: 'Accessible Vehicles',
    icon: <Car className="w-6 h-6 text-primary-600" data-id="element-801" />,
    description: 'Wheelchair accessible vans available upon request for any trip.'
  }, {
    title: 'Affordable Pricing',
    icon: <CheckCircle2 className="w-6 h-6 text-primary-600" data-id="element-802" />,
    description: 'Transparent rates with no hidden fees. Competitive pricing for all services.'
  }, {
    title: 'Punctual Service',
    icon: <Clock className="w-6 h-6 text-primary-600" data-id="element-803" />,
    description: 'We value your time. Our drivers arrive promptly to ensure you are never late.'
  }, {
    title: 'Door-to-Door Care',
    icon: <Heart className="w-6 h-6 text-primary-600" data-id="element-804" />,
    description: 'Assistance from your front door to your destination, ensuring safety at every step.'
  }, {
    title: 'Flexible Scheduling',
    icon: <Calendar className="w-6 h-6 text-primary-600" data-id="element-805" />,
    description: 'Book one-time trips or set up recurring daily or weekly transportation.'
  }];
  const steps = [{
    number: '01',
    title: 'Request a Ride',
    description: 'Call us or use our online booking form to request your transportation.'
  }, {
    number: '02',
    title: 'Confirm Details',
    description: 'We will confirm your pickup time, location, and any special requirements.'
  }, {
    number: '03',
    title: 'Enjoy the Ride',
    description: 'Our professional driver will arrive on time to get you safely to your destination.'
  }];
  return <div className="bg-white min-h-screen" data-id="element-806">
      {/* Hero Header */}
      <div className="bg-primary-900 text-white py-16 md:py-24" data-id="element-807">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-808">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6" data-id="element-809">
            Non-Medical Transportation
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-xl text-primary-100 max-w-3xl mx-auto leading-relaxed" data-id="element-810">
            Safe, reliable, and affordable transportation for everyday needs —
            no medical referral required.
          </motion.p>
        </div>
      </div>

      {/* Intro Section */}
      <section className="py-16 lg:py-24" data-id="element-811">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-812">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-813">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-814">
              <h2 className="text-3xl font-bold text-warm-900 mb-6" data-id="element-815">
                Everyday Mobility Made Easy
              </h2>
              <div className="space-y-4 text-lg text-warm-700 leading-relaxed" data-id="element-816">
                <p data-id="element-817">
                  Non-medical transportation (NMT) provides safe, dependable
                  rides for individuals who need help getting around but don't
                  require medical supervision during transport.
                </p>
                <p data-id="element-818">
                  Whether it's a trip to the grocery store, a visit to a friend,
                  or daily school pick-ups for your children, our professional
                  drivers ensure you arrive safely and on time. We believe that
                  lack of transportation should never prevent you from living an
                  active, independent life.
                </p>
                <p data-id="element-819">
                  Our fleet includes comfortable sedans, spacious vans, and
                  fully wheelchair-accessible vehicles to accommodate any
                  passenger requirement.
                </p>
              </div>
              <div className="mt-8" data-id="element-820">
                <Link to="/booking" data-id="element-821">
                  <Button size="lg" className="mr-4" data-id="element-822">
                    Book a Ride
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative" data-id="element-823">
              <img src="https://cdn.magicpatterns.com/uploads/kT5XfZgjwAS6LgTyP6B8dW/pexels-mingao-welfarecar-555708794-18247486.jpg" alt="Wheelchair accessible transportation van" className="rounded-2xl shadow-xl w-full h-[400px] object-cover" data-id="element-824" />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg hidden md:block" data-id="element-825">
                <div className="flex items-center gap-4" data-id="element-826">
                  <div className="bg-primary-100 p-3 rounded-full" data-id="element-827">
                    <Users className="w-8 h-8 text-primary-600" data-id="element-828" />
                  </div>
                  <div data-id="element-829">
                    <p className="text-sm text-warm-500 font-medium uppercase tracking-wider" data-id="element-830">
                      Serving
                    </p>
                    <p className="text-xl font-bold text-warm-900" data-id="element-831">
                      All Ages & Abilities
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 lg:py-24 bg-warm-50" data-id="element-832">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-833">
          <div className="text-center mb-16" data-id="element-834">
            <h2 className="text-3xl md:text-4xl font-bold text-warm-900 mb-4" data-id="element-835">
              Our Non-Medical Transport Services
            </h2>
            <p className="text-lg text-warm-600 max-w-2xl mx-auto" data-id="element-836">
              Comprehensive transportation solutions tailored to your daily life
              and specific requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-837">
            {services.map((service, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} data-id="element-838">
                <Card className="h-full border-none shadow-md hover:shadow-xl transition-shadow duration-300" data-id="element-839">
                  <CardBody className="p-8 flex flex-col h-full" data-id="element-840">
                    <div className="bg-primary-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6" data-id="element-841">
                      {service.icon}
                    </div>
                    <h3 className="text-xl font-bold text-warm-900 mb-3" data-id="element-842">
                      {service.title}
                    </h3>
                    <p className="text-warm-600 mb-6 flex-grow" data-id="element-843">
                      {service.description}
                    </p>
                    <ul className="space-y-2" data-id="element-844">
                      {service.features.map((feature, i) => <li key={i} className="flex items-start text-sm text-warm-700" data-id="element-845">
                          <CheckCircle2 className="w-4 h-4 text-primary-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-846" />
                          <span data-id="element-847">{feature}</span>
                        </li>)}
                    </ul>
                  </CardBody>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 lg:py-24" data-id="element-848">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-849">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-850">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-851">
              <img src="https://cdn.magicpatterns.com/uploads/d5zXDrhBCX1EnU4dT6JqNq/pexels-yankrukov-8617515.jpg" alt="Children being picked up for school run" className="rounded-2xl shadow-xl w-full h-[500px] object-cover" data-id="element-852" />
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-853">
              <h2 className="text-3xl font-bold text-warm-900 mb-6" data-id="element-854">
                Why Choose Gozie Transportation?
              </h2>
              <p className="text-lg text-warm-600 mb-8" data-id="element-855">
                We don't just provide rides; we provide peace of mind. Our
                commitment to safety, reliability, and customer care sets us
                apart.
              </p>

              <div className="grid sm:grid-cols-2 gap-6" data-id="element-856">
                {benefits.map((benefit, index) => <div key={index} className="flex flex-col" data-id="element-857">
                    <div className="flex items-center mb-2" data-id="element-858">
                      {benefit.icon}
                      <h4 className="text-lg font-bold text-warm-900 ml-3" data-id="element-859">
                        {benefit.title}
                      </h4>
                    </div>
                    <p className="text-warm-600 text-sm ml-9" data-id="element-860">
                      {benefit.description}
                    </p>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 lg:py-24 bg-warm-50" data-id="element-861">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-862">
          <div className="text-center mb-16" data-id="element-863">
            <h2 className="text-3xl font-bold text-warm-900 mb-4" data-id="element-864">
              How It Works
            </h2>
            <p className="text-lg text-warm-600 max-w-2xl mx-auto" data-id="element-865">
              Booking your non-medical transportation is simple and
              straightforward.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-866">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-warm-200 z-0" data-id="element-867"></div>

            {steps.map((step, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.2
          }} className="relative z-10 flex flex-col items-center text-center" data-id="element-868">
                <div className="w-24 h-24 rounded-full bg-white shadow-lg flex items-center justify-center border-4 border-primary-50 mb-6" data-id="element-869">
                  <span className="text-3xl font-extrabold text-primary-600" data-id="element-870">
                    {step.number}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-warm-900 mb-3" data-id="element-871">
                  {step.title}
                </h3>
                <p className="text-warm-600" data-id="element-872">{step.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-600 py-16 lg:py-20" data-id="element-873">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-874">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-875">
            Ready to Book Your Ride?
          </h2>
          <p className="text-xl text-primary-100 mb-10 max-w-2xl mx-auto" data-id="element-876">
            Contact us today to schedule your transportation or get a free quote
            for our services.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-877">
            <Link to="/booking" data-id="element-878">
              <Button size="lg" variant="ghost" className="!bg-white !text-primary-900 hover:!bg-warm-50 w-full sm:w-auto text-lg px-8 py-4" data-id="element-879">
                Book Transportation
              </Button>
            </Link>
            <a href="tel:2404132157" data-id="element-880">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto text-lg px-8 py-4" data-id="element-881">
                <Phone className="w-5 h-5 mr-2" data-id="element-882" />
                Call (240) 413-2157
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>;
}